import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReportUserAvatar from './ReportUserAvatar';
import { getUserReports } from '../services/reportService';
import {
  formatDate,
  formatTime,
  getAccountStatus,
  getReportStatus,
  pluralize,
} from '../constants/reports';

const DETAILS_PREVIEW_LENGTH = 220;

function ReportDetailsText({ text }) {
  const [expanded, setExpanded] = useState(false);

  if (!text) return <span className="report-details-empty">—</span>;

  const isLong = text.length > DETAILS_PREVIEW_LENGTH;
  const shown = isLong && !expanded ? `${text.slice(0, DETAILS_PREVIEW_LENGTH).trimEnd()}…` : text;

  return (
    <p className="report-details-text">
      {shown}
      {isLong && (
        <button type="button" className="report-details-toggle" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </p>
  );
}

function DrawerSkeleton() {
  return (
    <div className="report-drawer-skeleton">
      <div className="report-skeleton-line gc-skeleton" style={{ width: '60%' }} />
      <div className="report-skeleton-line gc-skeleton" style={{ width: '40%' }} />
      <div className="report-skeleton-block gc-skeleton" />
      <div className="report-skeleton-block gc-skeleton" />
    </div>
  );
}

// Side panel with every report filed against one user. A 404 means the user has no
// reports; `onNotFound(message)` is called and the caller should close the drawer.
// `updated` carries the result of a block/dismiss taken elsewhere so the panel stays in sync.
export default function ReportDetailsDrawer({ userId, updated, onClose, onNotFound, onAction }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function loadDetail() {
      setLoading(true);
      setError('');

      try {
        const response = await getUserReports(userId);
        if (cancelled) return;
        setDetail(response.data || null);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 404) {
          onNotFound?.('No reports found for this user.');
          return;
        }
        setError(err.message || 'Failed to load reports.');
        setDetail(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDetail();
    return () => {
      cancelled = true;
    };
    // onNotFound is intentionally left out so a new callback identity doesn't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, reloadKey]);

  // Apply a block/dismiss result right away, then refetch for the server's view.
  useEffect(() => {
    if (!updated || updated.userId !== userId) return;
    setDetail((prev) => prev && {
      ...prev,
      user: { ...prev.user, ...(updated.user || {}) },
      pendingCount: 0,
      reports: (prev.reports || []).map((r) => ({ ...r, status: 'reviewed' })),
    });
    getUserReports(userId)
      .then((response) => {
        if (response?.data) setDetail(response.data);
      })
      .catch(() => {});
  }, [updated, userId]);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const user = detail?.user;
  const reports = Array.isArray(detail?.reports) ? detail.reports : [];
  const pendingCount = detail?.pendingCount ?? 0;
  const accountStatus = user ? getAccountStatus(user) : null;
  const isBlocked = user?.isBlocked === 1;

  return (
    <div className="report-drawer-overlay" onClick={onClose}>
      <aside
        className="report-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Report details"
      >
        <div className="report-drawer-top">
          <h3 className="report-drawer-title">Report details</h3>
          <button type="button" className="action-btn" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="report-drawer-body">
          {loading && !detail ? (
            <DrawerSkeleton />
          ) : error ? (
            <div className="report-drawer-error">
              <p>{error}</p>
              <button type="button" className="btn btn-secondary btn-sm" onClick={retry}>Retry</button>
            </div>
          ) : user ? (
            <>
              <div className="report-drawer-header">
                <ReportUserAvatar user={user} className="report-drawer-avatar" />
                <div className="report-drawer-user">
                  <div className="report-drawer-name-row">
                    <span className="report-drawer-name">{user.name || '—'}</span>
                    <span className={`status-badge status-${accountStatus.className}`}>{accountStatus.label}</span>
                  </div>
                  <span className="user-cell-email">{user.email || '—'}</span>
                  {user.locationCity && <span className="user-cell-email">{user.locationCity}</span>}
                  <span className="report-drawer-counts">
                    {pluralize(detail.reportCount ?? reports.length, 'report')} · {pendingCount} pending
                  </span>
                  <Link className="report-link" to={`/users/${user.id}`}>View user profile →</Link>
                </div>
              </div>

              {isBlocked && (
                <div className="report-drawer-note">
                  This user is blocked. <Link className="report-link" to={`/users/${user.id}`}>Unblock from the Users page</Link>
                </div>
              )}

              <h4 className="report-drawer-section-title">Reports ({reports.length})</h4>
              {reports.length === 0 ? (
                <div className="gift-card-retry-empty">No reports.</div>
              ) : (
                <ul className="report-list">
                  {reports.map((report) => {
                    const reporter = report.reporter || {};
                    const status = getReportStatus(report.status);
                    return (
                      <li key={report.id} className="report-item">
                        <div className="report-item-head">
                          <div className="user-cell">
                            <ReportUserAvatar user={reporter} />
                            <div className="user-cell-info">
                              {reporter.id ? (
                                <Link className="user-cell-name report-reporter-link" to={`/users/${reporter.id}`}>
                                  {reporter.name || '—'}
                                </Link>
                              ) : (
                                <span className="user-cell-name">{reporter.name || '—'}</span>
                              )}
                              <span className="user-cell-email">{reporter.email || ''}</span>
                            </div>
                          </div>
                          <span className={`status-badge status-${status.className}`}>{status.label}</span>
                        </div>
                        <div className="report-item-meta">
                          {report.reason && <span className="report-reason-chip">{report.reason}</span>}
                          <span className="report-item-date">
                            {formatDate(report.createdAt)} {formatTime(report.createdAt)}
                          </span>
                        </div>
                        <ReportDetailsText text={report.details} />
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          ) : null}
        </div>

        {user && pendingCount > 0 && (
          <div className="report-drawer-footer">
            <button type="button" className="btn btn-secondary" onClick={() => onAction('dismiss', user)}>
              Dismiss
            </button>
            {!isBlocked && (
              <button type="button" className="btn btn-danger" onClick={() => onAction('block', user)}>
                Block user
              </button>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
